import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ExternalLink, Upload, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  corSituacao, motivoAgora, podeImportar, prazoVencido, resumoLista, rotuloSituacao,
} from './listaLL';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lista LL (Adnéia, 29/10/2026): o analista importa de manhã o arquivo dos tickets em "Aguardando
// Seller" do portal da LL; o ELO acha ou abre o atendimento de cada um (routes/lista_ll.py). Aqui ele
// vê a lista pelo prazo, abre cada atendimento numa aba nova e marca "Conferido".
export default function ListaLLModal({ onClose, getAuthHeader, user }) {
  const [lista, setLista] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [importando, setImportando] = useState(false);
  const [soPendentes, setSoPendentes] = useState(false);
  const arquivoRef = useRef(null);
  // O getAuthHeader do AuthContext muda a cada render: pelo ref, a lista carrega só ao abrir.
  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/lista-ll`, { headers: authRef.current() });
      setLista(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar a Lista LL');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const importar = async (ev) => {
    const arquivo = ev.target.files && ev.target.files[0];
    ev.target.value = '';
    if (!arquivo) return;
    setImportando(true);
    try {
      const corpo = new FormData();
      corpo.append('file', arquivo);
      const { data } = await axios.post(`${API_URL}/api/lista-ll/importar`, corpo, { headers: authRef.current() });
      const r = data.resumo || {};
      const abertos = (r.aberto || 0) + (r.aberto_sem_entrega || 0);
      const jaTinham = (r.ja_tinha || 0) + (r.ja_tinha_entrega || 0);
      toast.success(`Lista LL importada: ${abertos} atendimento(s) aberto(s), ${jaTinham} já tinham`
        + (r.nao_abriu ? `, ${r.nao_abriu} não abriram` : ''));
      await carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao importar o arquivo');
    } finally {
      setImportando(false);
    }
  };

  const marcar = async (t, valor) => {
    try {
      await axios.put(`${API_URL}/api/lista-ll/${lista.data}/${t.ticket}/conferido`, { conferido: valor },
        { headers: authRef.current() });
      setLista(l => ({
        ...l,
        tickets: l.tickets.map(x => (x.ticket === t.ticket
          ? { ...x, conferido: valor, conferido_por: valor ? (user?.name || '') : '' } : x)),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao marcar');
    }
  };

  const abrirAtendimento = (t) => {
    if (t.chamado_id) window.open(`/chamados/editar/${t.chamado_id}`, '_blank', 'noopener');
  };

  const tickets = (lista?.tickets || []).filter(t => !soPendentes || !t.conferido);
  const r = resumoLista(lista?.tickets);
  const dia = lista?.data ? lista.data.split('-').reverse().join('/') : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-6xl mx-4 max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()} data-testid="modal-lista-ll">
        <div className="flex items-start justify-between gap-3 p-4 border-b">
          <div>
            <h2 className="font-bold text-base">📋 Lista LL {dia && `· ${dia}`}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Tickets em Aguardando Seller no portal da LL (1 dia útil para responder), pelo prazo.
              {lista?.importada && ` Importada por ${lista.importado_por || '?'} às ${lista.importado_em_brt || '?'}.`}
            </p>
            {lista?.importada && (
              <p className="text-xs mt-1">
                <b>{r.total}</b> tickets · <b>{r.conferidos}</b> conferidos · {r.abertos} abertos agora
                {r.sem_entrega > 0 && ` (${r.sem_entrega} sem entrega)`} · {r.ja_tinham} já tinham atendimento
                {r.nao_abriu > 0 && <span className="text-red-700 dark:text-red-400"> · {r.nao_abriu} não abriram</span>}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {lista?.importada && (
              <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                <Checkbox checked={soPendentes} onCheckedChange={v => setSoPendentes(!!v)} />
                Só os não conferidos
              </label>
            )}
            {podeImportar(lista, user) && (
              <>
                <input ref={arquivoRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={importar}
                  data-testid="input-lista-ll" />
                <Button size="sm" variant="outline" disabled={importando || carregando}
                  onClick={() => arquivoRef.current && arquivoRef.current.click()}>
                  <Upload className="h-4 w-4 mr-1" />
                  {importando ? 'Importando...' : lista?.importada ? 'Importar de novo (admin)' : 'Importar arquivo do portal'}
                </Button>
              </>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="overflow-auto flex-1">
          {carregando ? (
            <p className="p-6 text-sm text-muted-foreground">Carregando...</p>
          ) : !lista?.importada ? (
            <p className="p-6 text-sm text-muted-foreground">
              A lista de hoje ainda não foi importada. No portal da LL, baixe o arquivo de todos os tickets e
              clique em "Importar arquivo do portal". O ELO abre o atendimento de quem ainda não tem, em
              "Ag. Conferência LL".
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 border-b">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Prazo</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Ticket</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Atendimento</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Entrega</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Motivo agora</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Na importação</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Assunto</th>
                  <th className="px-3 py-2 text-center font-medium text-slate-600">Conferido</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map(t => (
                  <tr key={t.ticket} className={`border-b hover:bg-slate-50 dark:hover:bg-slate-800/30 ${t.conferido ? 'opacity-50' : ''}`}>
                    <td className={`px-3 py-1.5 whitespace-nowrap text-xs ${prazoVencido(t.prazo_sla) && !t.conferido ? 'text-red-700 dark:text-red-400 font-semibold' : ''}`}
                      title={t.criado_em ? `Criado em ${t.criado_em}` : ''}>
                      {t.prazo_sla || '?'}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs">{t.ticket}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      {t.chamado_id ? (
                        <button onClick={() => abrirAtendimento(t)} className="text-blue-700 dark:text-blue-400 hover:underline inline-flex items-center gap-1 font-mono text-xs">
                          {t.id_atendimento} <ExternalLink className="h-3 w-3" />
                        </button>
                      ) : <span className="text-xs text-muted-foreground">-</span>}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs" title={(t.outras_entregas || []).length ? `Outras entregas do mesmo pedido: ${t.outras_entregas.join(', ')}` : ''}>
                      {t.numero_pedido || '-'}{(t.outras_entregas || []).length > 0 && ` (+${t.outras_entregas.length})`}
                    </td>
                    <td className="px-3 py-1.5 text-xs whitespace-nowrap">{motivoAgora(t)}</td>
                    <td className={`px-3 py-1.5 text-xs ${corSituacao(t.situacao)}`} title={t.erro || t.como_achou || ''}>
                      {rotuloSituacao(t.situacao)}
                    </td>
                    <td className="px-3 py-1.5 text-xs max-w-[260px] truncate" title={t.assunto}>{t.assunto}</td>
                    <td className="px-3 py-1.5 text-center" title={t.conferido_por ? `Conferido por ${t.conferido_por}` : ''}>
                      <Checkbox checked={!!t.conferido} onCheckedChange={v => marcar(t, !!v)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
